import { createTheme, PaletteMode } from '@mui/material'
import { ruRU } from '@mui/x-date-pickers/locales'

import { breakpointsKeys, breakpointsValues } from './breakpoints.ts'
import { createResponsiveTypography } from './typography.ts'
import { createComponentsOverride } from './components.tsx'
import { createPalette } from './palette.ts'

// Augment the palette to include an ochre color
declare module '@mui/material/styles' {
  interface TypeText {
    primary: string
    secondary: string
    tertiary: string
    link: string
    onControls: string
    onControlsDark: string
    disabled: string
  }

  interface TypeBackground {
    // main in DS
    default: string
    // secondary in DS
    paper: string

    blue: string
    third: string
    thirdHover: string
    green: string
    greenHover: string
    footer: string
    error: string
  }

  interface Palette {
    white: Palette['primary']
    icons: {
      main: string
      secondary: string
      onControls: string
      green: string
    }
    lines: {
      dividing: string
      grey: string
      green: string
      red: string
    }
    chip: {
      info: {
        bg: string
        text: string
      }
      warning: {
        bg: string
        text: string
      }
      success: {
        bg: string
        text: string
      }
      error: {
        bg: string
        text: string
      }
    }
  }

  interface PaletteOptions {
    white?: PaletteOptions['primary']
    icons: {
      main: string
      secondary: string
      onControls: string
      green: string
      yellow: string
    }
    lines: {
      dividing: string
      grey: string
      green: string
      red: string
    }
    infomer: {
      warning: {
        bg: string
        text: string
      }
    }
    subtitle: {
      primary: string
    }
    linkPage: {
      active: string
      notActive: string
    }
    linkPageText: {
      active: string
      notActive: string
    }
    chip: {
      info: {
        bg: string
        text: string
      }
      warning: {
        bg: string
        text: string
      }
      success: {
        bg: string
        text: string
      }
      error: {
        bg: string
        text: string
      }
    }
  }

  interface TypographyVariants {
    infographic: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    infographic?: React.CSSProperties
  }

  interface BreakpointOverrides {
    xs: true
    sm: true
    md: true
    lg: true
    xl: true
    px526: true
    px975: true
    px1240: true
    px1350: true
    px1640: true
  }
}

// Update the Button's color options to include an ochre option
declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    white: true
    error: false
    info: false
    success: false
    warning: false
  }
}

// Update the Button's color options to include an ochre option
declare module '@mui/material/Alert' {
  interface AlertPropsColorOverrides {
    error: true
    info: true
    success: true
    warning: false
  }
}

// Update the Typography's color options to include an ochre option
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    infographic: true
  }
}

const darkPalette = createPalette(true)
const lightPalette = createPalette(false)
export type Palette = typeof lightPalette | typeof darkPalette

type Join<K, P> = K extends string ? (P extends string ? `${K}${'' extends P ? '' : '.'}${P}` : never) : never
type PathToValue<T> = T extends object ? { [K in keyof T]?: Join<K, PathToValue<T[K]>> }[keyof T] : ''
export type PaletteLiteral = PathToValue<Palette> | 'inherit'

export const getModuleTheme = (mode: PaletteMode) => {
  const breakpointsTheme = createTheme({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    breakpoints: { values: breakpointsValues, keys: breakpointsKeys },
  })

  const isDark = mode === 'dark'
  const typography = createResponsiveTypography(breakpointsTheme)
  const palette = isDark ? darkPalette : lightPalette

  return createTheme(
    {
      palette,
      typography,
      breakpoints: breakpointsTheme.breakpoints,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      components: createComponentsOverride(isDark, palette, typography),
    },
    ruRU
  )
}
